import { createStore } from "@crialume/core";
import type { ILayoutService, LayoutSlotName, PageMeta } from "@crialume/sdk";
import { useSyncExternalStore } from "react";

const pageMetaStore = createStore<{ meta: PageMeta }>({ meta: {} });
const slotStore = createStore<{ slots: Partial<Record<LayoutSlotName, Element>> }>({ slots: {} });

/**
 * Implementação do ILayoutService. "Último a registrar vence"; a limpeza só
 * apaga se ainda for o meta vigente (evita a página nova ser apagada pelo
 * unmount atrasado da anterior).
 */
export const layoutService: ILayoutService = {
  setPageMeta(meta) {
    pageMetaStore.setState({ meta });
    return () => {
      if (pageMetaStore.getState().meta === meta) pageMetaStore.setState({ meta: {} });
    };
  },
  getSlot: (name) => slotStore.getState().slots[name] ?? null,
  subscribeSlots: (listener) => slotStore.subscribe(() => listener()),
};

/**
 * Ref callback para o elemento que hospeda uma região do layout
 * (`<div ref={layoutSlotRef("page-actions")} />`). Páginas preenchem via `<PageActions>`.
 */
export function layoutSlotRef(name: LayoutSlotName) {
  // Referência estável por região: um callback novo a cada render faria o
  // React desanexar/reanexar o elemento e remontar o portal das páginas.
  let ref = slotRefs.get(name);
  if (!ref) {
    ref = (element: HTMLElement | null) => {
      const { slots } = slotStore.getState();
      if ((slots[name] ?? null) === element) return;
      const next = { ...slots };
      if (element) next[name] = element;
      else delete next[name];
      slotStore.setState({ slots: next });
    };
    slotRefs.set(name, ref);
  }
  return ref;
}

const slotRefs = new Map<LayoutSlotName, (element: HTMLElement | null) => void>();

const subscribe = (onChange: () => void) => pageMetaStore.subscribe(onChange);
const getMeta = () => pageMetaStore.getState().meta;

export function usePageMetaState(): PageMeta {
  return useSyncExternalStore(subscribe, getMeta, getMeta);
}
